import React, { useMemo, useState } from 'react';
import {
  Button,
  Tabs,
  Segmented,
  Alert,
  Tooltip,
  Dropdown,
  App,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useResponsive } from '../hooks/useResponsive';
import { useCurrency } from '../hooks/useCurrency';
import { useAppSelector } from '../hooks/redux';
import { useRestaurantTimeZone } from '../hooks/useTimeZone';
import { formatDateOnly } from '../utils/dateHelpers';
import {
  useGetTodaysSalesReportQuery,
  useGetThisWeekSalesReportQuery,
  useGetThisMonthSalesReportQuery,
  useGetWeeklySalesReportQuery,
  useGetProductSalesBreakdownQuery,
  useGetEmployeePerformanceQuery,
  useGetCashierPerformanceQuery,
} from '../services/reportsApi';
import {
  exportCSV,
  exportExcel,
  exportPDF,
  getPeriodLabel,
  ExportConfig,
} from '../utils/reportExport';
import ReportSummary from '../components/reports/ReportSummary';
import ProductAnalysis from '../components/reports/ProductAnalysis';
import EmployeePerformance from '../components/reports/EmployeePerformance';
import CashMovementsReport from '../components/reports/CashMovementsReport';
import DailyMenuCountsReport from '../components/dailyMenu/DailyMenuCountsReport';
import { useGetBoolSettingQuery } from '../services/settingsApi';
import MobileDateRangePicker from '../components/common/MobileDateRangePickerV2';
import '../components/reports/reports.css';
import type { GuestSummary } from '../types/reports';
import { formatGuests } from '../utils/guests';

const EMPTY_GUESTS: GuestSummary = {
  totalGuests: 0,
  adults: 0,
  children: 0,
  dineInOrders: 0,
  averageGuestsPerOrder: 0,
  averageSpendPerGuest: 0,
};

// 3.5 por mesa, pero 4 (no 4.0) cuando el promedio es entero
const formatGuestsPerTable = (n: number) =>
  (Math.round(n * 10) / 10).toLocaleString('en-US', { maximumFractionDigits: 1 });

const Reports: React.FC = () => {
  // App.useApp() y no el import estatico: bajo React 19 el estatico no dibuja
  const { message } = App.useApp();
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { startOfDay, startOfWeek, startOfMonth, formatInTimeZone } = useRestaurantTimeZone();

  const [activeTab, setActiveTab] = useState('resumen');
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [customDateRange, setCustomDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  const { data: menuDelDiaSetting } = useGetBoolSettingQuery('MenuDelDiaActivo');
  const menuDelDiaEnabled = menuDelDiaSetting?.value === true;

  // Quick reports queries
  const { data: todayReport, isLoading: todayLoading, error: todayError } = useGetTodaysSalesReportQuery(
    currentRestaurant?.id || '',
    { skip: !currentRestaurant?.id }
  );

  const { data: weekReport, isLoading: weekLoading, error: weekError } = useGetThisWeekSalesReportQuery(
    currentRestaurant?.id || '',
    { skip: !currentRestaurant?.id }
  );

  const { data: monthReport, isLoading: monthLoading, error: monthError } = useGetThisMonthSalesReportQuery(
    currentRestaurant?.id || '',
    { skip: !currentRestaurant?.id }
  );

  // Query for custom date range (uses weekly endpoint with custom dates)
  const { data: customReport, isLoading: customLoading, error: customError } =
    useGetWeeklySalesReportQuery(
      {
        restaurantId: currentRestaurant?.id || '',
        weekStartDate: customDateRange?.[0]?.format('YYYY-MM-DD') || '',
        weekEndDate: customDateRange?.[1]?.format('YYYY-MM-DD'),
      },
      {
        skip: !currentRestaurant?.id ||
              selectedPeriod !== 'custom' ||
              !customDateRange?.[0] ||
              !customDateRange?.[1]
      }
    );

  // Calculate date range for product breakdown query using restaurant timezone
  const dateRange = useMemo(() => {
    const today = startOfDay();

    if (customDateRange && customDateRange[0] && customDateRange[1]) {
      return {
        fromDate: customDateRange[0].format('YYYY-MM-DD'),
        toDate: customDateRange[1].format('YYYY-MM-DD'),
      };
    }

    switch (selectedPeriod) {
      case 'today':
        return {
          fromDate: today.format('YYYY-MM-DD'),
          toDate: today.format('YYYY-MM-DD'),
        };
      case 'week':
        return {
          fromDate: startOfWeek().format('YYYY-MM-DD'),
          toDate: today.format('YYYY-MM-DD'),
        };
      case 'month':
        return {
          fromDate: startOfMonth().format('YYYY-MM-DD'),
          toDate: today.format('YYYY-MM-DD'),
        };
      default:
        return {
          fromDate: today.format('YYYY-MM-DD'),
          toDate: today.format('YYYY-MM-DD'),
        };
    }
  }, [selectedPeriod, customDateRange, startOfDay, startOfWeek, startOfMonth]);

  // Query for products breakdown based on selected date range (for exports)
  const { data: productsBreakdown } = useGetProductSalesBreakdownQuery(
    {
      restaurantId: currentRestaurant?.id || '',
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    },
    { skip: !currentRestaurant?.id || !dateRange.fromDate || !dateRange.toDate }
  );

  // Query for employee performance based on selected date range (for exports)
  const { data: employeesData } = useGetEmployeePerformanceQuery(
    {
      restaurantId: currentRestaurant?.id || '',
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    },
    { skip: !currentRestaurant?.id || !dateRange.fromDate || !dateRange.toDate }
  );

  // Query for cashier performance based on selected date range (for exports)
  const { data: cashiersData } = useGetCashierPerformanceQuery(
    {
      restaurantId: currentRestaurant?.id || '',
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    },
    { skip: !currentRestaurant?.id || !dateRange.fromDate || !dateRange.toDate }
  );

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
  };

  const handleExportReport = (format: 'csv' | 'excel' | 'pdf') => {
    if (!currentReport.data) {
      return;
    }

    const report = currentReport.data as any;
    const periodLabel = getPeriodLabel(selectedPeriod, customDateRange);
    const timestamp = formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm');

    const topProducts = productsBreakdown && productsBreakdown.length > 0
      ? productsBreakdown.slice(0, 10)
      : report.topProducts || [];
    const reportGuests: GuestSummary = report.guests ?? EMPTY_GUESTS;

    const config: ExportConfig = {
      reportTitle: 'Reporte General de Ventas',
      periodLabel,
      restaurantName: currentRestaurant?.name || 'N/A',
      timestamp,
      summaryCards: [
        { label: 'Ingresos Totales', value: `${currencySymbol} ${(report.totalRevenue || 0).toFixed(2)}` },
        { label: 'Descuentos', value: `${currencySymbol} ${(report.totalDiscounts || 0).toFixed(2)}` },
        { label: 'Ingreso Neto', value: `${currencySymbol} ${((report.netRevenue ?? ((report.totalRevenue || 0) - (report.totalDiscounts || 0)))).toFixed(2)}` },
        { label: 'Total Órdenes', value: `${report.totalOrders || 0}` },
        { label: 'Ticket Promedio', value: `${currencySymbol} ${(report.averageOrderValue || 0).toFixed(2)}` },
        { label: 'Comensales', value: `${reportGuests.totalGuests}` },
        { label: 'Consumo Neto por Persona', value: `${currencySymbol} ${reportGuests.averageSpendPerGuest.toFixed(2)}` },
      ],
      sections: [],
    };

    // Comensales: gente atendida en mesa, más allá del número de tickets
    config.sections.push({
      title: 'Comensales (solo mesas)',
      titleStyle: 'section',
      headers: ['Concepto', 'Valor'],
      rows: [
        ['Adultos', `${reportGuests.adults}`],
        ['Niños', `${reportGuests.children}`],
        ['Total comensales', `${reportGuests.totalGuests}`],
        ['Mesas atendidas', `${reportGuests.dineInOrders}`],
        ['Promedio por mesa', formatGuestsPerTable(reportGuests.averageGuestsPerOrder)],
        ['Consumo neto por persona', `${currencySymbol} ${reportGuests.averageSpendPerGuest.toFixed(2)}`],
      ],
    });

    // Órdenes y comensales por día (o por hora, si el período es hoy)
    const breakdownRows: (string | number)[][] = selectedPeriod === 'today'
      ? (report.hourlyBreakdown || [])
          .filter((h: any) => h.orderCount > 0)
          .map((h: any) => [
            h.timeRange || `${h.hour}:00`, h.orderCount, h.guestCount ?? 0, `${currencySymbol} ${h.revenue.toFixed(2)}`,
          ])
      : (report.dailyBreakdown || []).map((d: any) => [
          formatDateOnly(d.date, 'DD/MM/YYYY'), d.orderCount, d.guestCount ?? 0, `${currencySymbol} ${d.revenue.toFixed(2)}`,
        ]);
    config.sections.push({
      title: selectedPeriod === 'today' ? 'Ventas por Hora' : 'Ventas por Día',
      titleStyle: 'section',
      headers: [selectedPeriod === 'today' ? 'Hora' : 'Día', 'Órdenes', 'Comensales', 'Ingresos'],
      rows: breakdownRows,
    });

    // Discounts & cancellations — makes courtesies/cancellations explicit so the
    // report reconciles against the cash count.
    config.sections.push({
      title: 'Descuentos y Cancelaciones',
      titleStyle: 'section',
      headers: ['Concepto', 'Cantidad', 'Monto'],
      rows: [
        ['Descuentos aplicados', '—', `${currencySymbol} ${(report.totalDiscounts || 0).toFixed(2)}`],
        ['Órdenes canceladas', `${report.cancelledOrdersCount || 0}`, `${currencySymbol} ${(report.cancelledOrdersAmount || 0).toFixed(2)}`],
        ['Ítems cancelados', `${report.cancelledItemsCount || 0}`, `${currencySymbol} ${(report.cancelledItemsAmount || 0).toFixed(2)}`],
        ['Consumo interno (costo insumos)', `${report.internalConsumptionCount || 0}`, `${currencySymbol} ${(report.internalConsumptionCost || 0).toFixed(2)}`],
      ],
    });

    // Top Products
    if (topProducts.length > 0) {
      config.sections.push({
        title: 'Productos Más Vendidos',
        titleStyle: 'section',
        headers: ['#', 'Producto', 'Categoría', 'Cantidad', 'Ingresos'],
        rows: topProducts.map((p: any, i: number) => [
          i + 1, p.productName, p.categoryName, p.quantitySold, `${currencySymbol} ${p.revenue.toFixed(2)}`,
        ]),
      });
    }

    // Payment Methods
    if (report.paymentMethods?.length > 0) {
      config.sections.push({
        title: 'Métodos de Pago',
        titleStyle: 'section',
        headers: ['Método', 'Transacciones', 'Monto'],
        rows: report.paymentMethods.map((m: any) => [
          m.displayName, m.count, `${currencySymbol} ${m.amount.toFixed(2)}`,
        ]),
      });
    }

    // Employees
    if (employeesData && employeesData.length > 0) {
      config.sections.push({
        title: 'Rendimiento de Meseros',
        titleStyle: 'employee',
        headers: ['Mesero', 'Órdenes', 'Ventas', 'Ticket Prom.', 'Propinas', '% Total'],
        rows: employeesData.map((e: any) => [
          e.userName, e.ordersCount, `${currencySymbol} ${e.totalSales.toFixed(2)}`,
          `${currencySymbol} ${e.averageTicket.toFixed(2)}`, `${currencySymbol} ${e.tips.toFixed(2)}`,
          `${e.salesPercentage.toFixed(1)}%`,
        ]),
      });
    }

    // Cashiers
    if (cashiersData && cashiersData.length > 0) {
      config.sections.push({
        title: 'Rendimiento de Cajeros',
        titleStyle: 'cashier',
        headers: ['Cajero', 'Turnos', 'Efectivo', 'Tarjeta', 'Transfer.', 'Total', 'Varianza'],
        rows: cashiersData.map((c: any) => [
          c.userName, c.shiftsCount, `${currencySymbol} ${c.cashSales.toFixed(2)}`,
          `${currencySymbol} ${c.cardSales.toFixed(2)}`, `${currencySymbol} ${c.transferSales.toFixed(2)}`,
          `${currencySymbol} ${c.totalSales.toFixed(2)}`, `${currencySymbol} ${c.averageVariance.toFixed(2)}`,
        ]),
      });
    }

    const prefix = 'reporte-general';
    if (format === 'csv') exportCSV(config, prefix);
    else if (format === 'excel') exportExcel(config, prefix);
    else exportPDF(config);
  };

  // Export functions moved to utils/reportExport.ts



  const handleCustomDateRange = (dates: any) => {
    if (!dates || !dates[0] || !dates[1]) {
      setCustomDateRange(null);
      return;
    }

    // VALIDACIÓN 1: start <= end
    if (dates[0].isAfter(dates[1])) {
      message.error('Fecha de inicio debe ser anterior a fecha de fin');
      return;
    }

    // VALIDACIÓN 2: max 365 días
    const daysDiff = dates[1].diff(dates[0], 'days');
    if (daysDiff > 365) {
      message.error('El rango máximo es 365 días');
      return;
    }

    // VALIDACIÓN 3: no fechas futuras
    if (dates[1].isAfter(dayjs())) {
      message.error('No se pueden generar reportes de fechas futuras');
      return;
    }

    setCustomDateRange(dates);
    setSelectedPeriod('custom');
  };

  const getCurrentReport = () => {
    switch (selectedPeriod) {
      case 'today':
        return { data: todayReport, loading: todayLoading, error: todayError };
      case 'week':
        return { data: weekReport, loading: weekLoading, error: weekError };
      case 'month':
        return { data: monthReport, loading: monthLoading, error: monthError };
      case 'custom':
        return { data: customReport, loading: customLoading, error: customError };
      default:
        return { data: todayReport, loading: todayLoading, error: todayError };
    }
  };

  const currentReport = getCurrentReport();

  // ----- KPI derivations -----
  const reportData = currentReport.data as any;

  // Revenue delta vs previous period (only the revenue comparison exists in the DTOs)
  const revenueDelta: number | null =
    selectedPeriod === 'today' ? reportData?.comparisonWithYesterday ?? null :
    selectedPeriod === 'week' ? reportData?.comparisonWithPreviousWeek ?? null :
    selectedPeriod === 'month' ? reportData?.comparisonWithPreviousMonth ?? null :
    null;
  const deltaBaseline =
    selectedPeriod === 'today' ? 'vs ayer' :
    selectedPeriod === 'week' ? 'vs semana anterior' :
    'vs mes anterior';

  // Peak: best hour (today) or best day (week/month/custom) — computed client-side
  const peak = useMemo(() => {
    if (!reportData) return null;
    if (selectedPeriod === 'today' && reportData.hourlyBreakdown?.length) {
      const best = reportData.hourlyBreakdown.reduce(
        (a: any, b: any) => (b.revenue > a.revenue ? b : a)
      );
      if (!best.revenue) return null;
      return { label: best.timeRange || `${best.hour}:00`, value: best.revenue };
    }
    if (reportData.dailyBreakdown?.length) {
      const best = reportData.dailyBreakdown.reduce(
        (a: any, b: any) => (b.revenue > a.revenue ? b : a)
      );
      if (!best.revenue) return null;
      return { label: formatDateOnly(best.date, 'DD/MM'), value: best.revenue };
    }
    return null;
  }, [reportData, selectedPeriod]);

  const money = (n: number, decimals = 2) =>
    `${currencySymbol}${(n || 0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

  // Comensales del período. El ?? cubre un API que todavía no los manda
  // (el frontend puede desplegarse antes que el backend).
  const guests: GuestSummary = reportData?.guests ?? EMPTY_GUESTS;

  const renderDelta = (value: number | null) => {
    if (value === null || value === undefined) return null;
    const up = value >= 0;
    return (
      <div className={`rep-kpi__delta ${up ? 'rep-kpi__delta--up' : 'rep-kpi__delta--down'}`}>
        {up ? '▲' : '▼'} {Math.abs(value).toFixed(1)}% {deltaBaseline}
      </div>
    );
  };

  return (
    <div>
      {/* Single bar: title + period pills + range + export */}
      <div className="rep-bar">
        <h2 className="rep-bar__title">Reportes</h2>
        <Segmented
          value={selectedPeriod === 'custom' ? undefined : selectedPeriod}
          onChange={(value) => handlePeriodChange(value as string)}
          options={[
            { label: 'Hoy', value: 'today' },
            { label: 'Esta semana', value: 'week' },
            { label: 'Este mes', value: 'month' },
          ]}
        />
        <MobileDateRangePicker
          value={customDateRange}
          onChange={handleCustomDateRange}
          placeholder={['Fecha inicio', 'Fecha fin']}
          style={{ width: isMobile ? '100%' : 240 }}
        />
        <div className="rep-bar__spacer" />
        <Tooltip title="Exporta ventas, productos, empleados y cajeros">
          <Dropdown menu={{ items: [
            { key: 'csv', label: 'CSV', onClick: () => handleExportReport('csv') },
            { key: 'excel', label: 'Excel', onClick: () => handleExportReport('excel') },
            { key: 'pdf', label: 'PDF', onClick: () => handleExportReport('pdf') },
          ]}}>
            <Button icon={<DownloadOutlined />}>Exportar</Button>
          </Dropdown>
        </Tooltip>
      </div>

      {/* KPIs with their delta integrated; values wear ink, color only on deltas */}
      {reportData && (
        <div className="rep-kpis">
          <div className="rep-kpi">
            <div className="rep-kpi__label">Ingresos</div>
            <div className="rep-kpi__value">{money(reportData.totalRevenue)}</div>
            {renderDelta(revenueDelta) || <div className="rep-kpi__sub">{deltaBaseline}: sin datos</div>}
          </div>
          <div className="rep-kpi">
            <div className="rep-kpi__label">Descuentos</div>
            <div className="rep-kpi__value">{money(reportData.totalDiscounts || 0)}</div>
            <div className="rep-kpi__sub">cortesías y descuentos</div>
          </div>
          <div className="rep-kpi">
            <div className="rep-kpi__label">Ingreso neto</div>
            <div className="rep-kpi__value">
              {money(reportData.netRevenue ?? ((reportData.totalRevenue || 0) - (reportData.totalDiscounts || 0)))}
            </div>
            <div className="rep-kpi__sub">ingresos − descuentos</div>
          </div>
          <div className="rep-kpi">
            <div className="rep-kpi__label">Órdenes</div>
            <div className="rep-kpi__value">{reportData.totalOrders || 0}</div>
            <div className="rep-kpi__sub">procesadas en el período</div>
          </div>
          <div className="rep-kpi">
            <div className="rep-kpi__label">Ticket promedio</div>
            <div className="rep-kpi__value">{money(reportData.averageOrderValue)}</div>
            <div className="rep-kpi__sub">por orden</div>
          </div>
          {/* Gente atendida, no tickets: una orden puede ser una mesa de 20 */}
          <Tooltip title={guests.totalGuests > 0
            ? `${formatGuests(guests.adults, guests.children)} en ${guests.dineInOrders} mesa${guests.dineInOrders !== 1 ? 's' : ''}. `
              + 'Solo mesas atendidas: para llevar y domicilio no cuentan. Por persona = venta neta, ya con descuentos.'
            : 'Solo cuentan las mesas atendidas: para llevar y domicilio no tienen comensales.'}>
            <div className="rep-kpi">
              <div className="rep-kpi__label">Comensales</div>
              <div className="rep-kpi__value">{guests.totalGuests}</div>
              <div className="rep-kpi__sub">
                {guests.totalGuests > 0
                  ? `${formatGuestsPerTable(guests.averageGuestsPerOrder)} por mesa · ${money(guests.averageSpendPerGuest)} por persona`
                  : 'sin mesas en el período'}
              </div>
            </div>
          </Tooltip>
          <div className="rep-kpi">
            <div className="rep-kpi__label">Cancelaciones</div>
            <div className="rep-kpi__value">{money(reportData.cancelledOrdersAmount || 0)}</div>
            <div className="rep-kpi__sub">
              {reportData.cancelledOrdersCount || 0} órdenes · {reportData.cancelledItemsCount || 0} ítems
            </div>
          </div>
          {((reportData.internalConsumptionCount || 0) > 0 || (reportData.internalConsumptionCost || 0) > 0) && (
            <div className="rep-kpi">
              <div className="rep-kpi__label">Consumo interno</div>
              <div className="rep-kpi__value">{money(reportData.internalConsumptionCost || 0)}</div>
              <div className="rep-kpi__sub">
                {reportData.internalConsumptionCount} órdenes · costo de insumos
              </div>
            </div>
          )}
          <div className="rep-kpi">
            <div className="rep-kpi__label">{selectedPeriod === 'today' ? 'Hora pico' : 'Día pico'}</div>
            <div className="rep-kpi__value">{peak ? peak.label : '—'}</div>
            <div className="rep-kpi__sub">{peak ? money(peak.value, 0) : 'sin ventas aún'}</div>
          </div>
        </div>
      )}

      {/* Tabs: Detallado + Gráficos merged into Resumen (same dataset, one place) */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size={isMobile ? 'middle' : 'large'}
        style={{ marginBottom: 24 }}
        items={[
          {
            key: 'resumen',
            label: 'Resumen',
            children: currentReport.error ? (
              <Alert
                message="Error al cargar reporte"
                description="No se pudo cargar el resumen del período"
                type="error"
                showIcon
              />
            ) : (
              <ReportSummary
                report={currentReport.data}
                loading={currentReport.loading}
                period={selectedPeriod}
              />
            ),
          },
          {
            key: 'products',
            label: 'Productos',
            children: (
              <ProductAnalysis
                selectedPeriod={selectedPeriod}
                customDateRange={customDateRange}
              />
            ),
          },
          {
            key: 'employees',
            label: 'Empleados',
            children: (
              <EmployeePerformance
                selectedPeriod={selectedPeriod}
                customDateRange={customDateRange}
              />
            ),
          },
          {
            key: 'cash-movements',
            label: 'Caja',
            children: (
              <CashMovementsReport
                selectedPeriod={selectedPeriod}
                customDateRange={customDateRange}
              />
            ),
          },
          ...(menuDelDiaEnabled
            ? [
                {
                  key: 'daily-menu-counts',
                  label: 'Menú del Día',
                  children: <DailyMenuCountsReport />,
                },
              ]
            : []),
        ]}
      />
    </div>
  );
};

export default Reports;